// "Send an approved message" in a Conversations reply bar (Phase 3, step 4 part 2, 2026-10-11).
//
// When the 24-hour WhatsApp window is closed a free-form message cannot be sent, but the office's own APPROVED
// messages (the ones it wrote under My Account -> WhatsApp -> "Add new message") still can. Staff pick one; the
// server decides everything that reaches the patient (see conversations-service lib/waOnboarding/customSend.js),
// so the browser sends ONLY the key.
//
// Pure and import-free on purpose: cypress/node-tests/whatsapp-approved-messages.test.js executes this exact file.
//
// WORDING RULE (Saumil, 2026-10-11): NEVER mention SMS, texting or any other way of sending. An office may have no
// SMS at all, and no office is nudged to change channel. Pinned by the test above.

// 2026-10-14: the picker lives IN the message-box area (it replaces the text box while the window is closed): a
// menu when there are several messages, the exact text as a preview, and ONE Send button. The preview is the
// confirmation, so there is no second "are you sure" step and no floating list.
export const APPROVED_CHOOSE_LABEL = 'Message';
export const APPROVED_PREVIEW_CAPTION = 'This is exactly what the patient receives.';
export const APPROVED_SEND_LABEL = 'Send message';
export const APPROVED_SENT_TOAST = 'Message sent';
export const APPROVED_FAILED_FALLBACK = "We couldn't send that message. Please try again in a moment.";

/** The most custom messages an office can have (the server enforces it; this only bounds what is rendered). */
export const MAX_APPROVED_LISTED = 10;

const CUSTOM_PREFIX = 'custom-';
const isText = (v) => typeof v === 'string' && v.trim() !== '';

/**
 * What the server answered (`{ messages: [{ key, title, preview }] }`) as a clean list. Anything that is not a
 * custom message with a title and a preview is dropped, never half-shown; a repeated key is listed once.
 *
 * @param {*} data the response body
 * @returns {Array<{key: string, title: string, preview: string}>}
 */
export function normalizeApprovedMessages(data) {
  const list = data && Array.isArray(data.messages) ? data.messages : [];
  const seen = new Set();
  const out = [];
  for (const m of list) {
    if (!m || typeof m !== 'object') continue;
    if (!isText(m.key) || !m.key.startsWith(CUSTOM_PREFIX) || m.key.length === CUSTOM_PREFIX.length) continue;
    if (!isText(m.title) || !isText(m.preview) || seen.has(m.key)) continue;
    seen.add(m.key);
    out.push({ key: m.key, title: m.title.trim(), preview: m.preview.trim() });
    if (out.length >= MAX_APPROVED_LISTED) break;
  }
  return out;
}

/**
 * The message the panel shows and Send would send: the one whose key is picked, else the FIRST in the list (so an
 * office with one message needs no picking at all), else null. A key that is no longer in the list (the list reloaded)
 * falls back to the first, never to a message the patient was not shown.
 *
 * @param {Array<{key: string}>} list the normalized approved messages
 * @param {string} key                the picked key, '' when nothing was picked
 * @returns {object|null}
 */
export function resolveChosenApproved(list, key) {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.find((m) => m && m.key === key) || list[0];
}

/**
 * The /send-message body for an approved message: the key and who is sending, NOTHING the patient will read. The
 * server ignores any text sent alongside a key, so none is sent.
 *
 * @param {object} args
 * @param {string} args.roomId       the open conversation
 * @param {string} args.key          the approved message's key
 * @param {string} args.siteID
 * @param {string} [args.authorName]
 * @param {string} [args.employeeName]
 * @param {string} args.clientNonce  one per attempt, so a double click cannot send twice
 */
export function buildApprovedSendPayload({ roomId, key, siteID, authorName, employeeName, clientNonce } = {}) {
  if (!isText(roomId)) throw new Error('buildApprovedSendPayload: roomId is required');
  if (!isText(key) || !key.startsWith(CUSTOM_PREFIX)) throw new Error('buildApprovedSendPayload: an approved message key is required');
  if (!isText(siteID)) throw new Error('buildApprovedSendPayload: siteID is required');
  if (!isText(clientNonce)) throw new Error('buildApprovedSendPayload: clientNonce is required');
  return {
    roomId,
    waTemplateKey: key,
    message: {
      author: authorName || 'TeamIO',
      attributes: { outbound: true, clientNonce, channel: 'whatsapp' },
    },
    backgroundTaskPayload: { siteID, employeeName: employeeName || '' },
  };
}

/**
 * The sentence to show when a send did not go. Prefers the server's own plain sentence (a 200 `{success:false}` body
 * or a rejected request's `response.data`); never a code, a stack or "undefined".
 */
export function approvedSendFailureMessage(body) {
  const message = body && typeof body === 'object' ? body.message : null;
  return isText(message) ? message.trim() : APPROVED_FAILED_FALLBACK;
}
